import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Button, Checkbox, Chip, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useContext, useMemo } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { ReactComponent as Db2Icon } from "../../../assets/icons/db-2.svg";
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import { TableContext } from '../../../contexts/TableContext';
import { scrollbarStyle } from '../../../shared/dashboard';
import AddCustomField from '../../atoms/AddCustomField';
import { FallbackLoading } from '../../templates/loader';

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        minWidth: 350,
        width: 350,
        height: 500,
        padding: '32px 24px',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid #e2e8f0`,
        transition: 'all 300ms ease-in-out',
        "&:hover": {
            borderColor: '#1eb6a8',
            boxShadow: '0 8px 24px rgba(30, 182, 168, 0.12)',
        },
    },
    mainIcon: {
        width: 20,
        height: 20,
        stroke: '#1eb6a8',
        fill: 'none',
        strokeWidth: '2',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
    },
    mainIconContainer: {
        width: 40,
        height: 40,
        background: '#f0fdf9',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #d1f5ee',
        flexShrink: 0,
    },
    subIcon: {
        width: 24,
        height: 24,
        fill: theme.colors.neutral['400'],
    },
    leftBulletIcon: {
        width: 24,
        height: 24,
        fill: theme.colors.primary['300'],
        backgroundColor: '#fff',
        transform: 'translateX(-12px)',
    },
    longLine: {
        width: '100%',
        borderBottom: `3px solid ${theme.colors.primary['200']}`,
        marginLeft: '-16px',
        marginRight: '-12px',
        zIndex: 1,
    },
    rightArrowIcon : {
        width: 32,
        height: 32,
        fill: theme.colors.primary['300'],
        zIndex: 1,
    },
    title: {
        fontFamily: 'Lato',
        fontSize: '1.1rem',
        fontWeight: 600,
        color: '#1e293b',
    },
    label: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 500,
        color: theme.colors.neutral['500'],
    },
    subText: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 400,
        color: theme.colors.neutral['500'],
    },
    link: {
        display: '-webkit-box',
        textDecoration: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-all',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
	iconContainer: {
		width: 40,
		height: 40,
		backgroundColor: '#f0fdf9',
		borderRadius: 10,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		border: '2px solid #d1f5ee'
	},
	icon: {
		fontSize: 20,
		color: '#1eb6a8'
	},
	description: {
		marginBottom: theme.spacing(2),
		lineHeight: 1.6
	},
	selectedTypeIndicator: {
		textAlign: 'center',
		marginBottom: theme.spacing(2),
		padding: theme.spacing(1, 1.5),
		backgroundColor: '#f0fdf9',
		borderRadius: 8,
		border: '1px solid #d1f5ee'
	},
	selectedTypeText: {
		fontSize: 12,
		color: '#1eb6a8',
		fontWeight: 600
	},
	fieldsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1),
		flex: 1
	},
	field: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		padding: theme.spacing(1.5, 1.75),
		backgroundColor: '#f8f9fc',
		borderRadius: 10,
		border: '1px solid transparent',
		transition: 'all 0.2s',
		'&:hover': {
		backgroundColor: '#f0fdf9',
		borderColor: '#d1f5ee'
		}
	},
	checkbox: {
		padding: 0,
		'&.Mui-checked': {
		color: '#1eb6a8'
		}
	},
	fieldInfo: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: 2
	},
	fieldHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: 2
	},
	fieldName: {
		fontSize: 14,
		fontWeight: 600,
		lineHeight: 1.2,
		color: '#1e293b',
	},
	fieldDescription: {
		fontSize: 12,
		lineHeight: 1.3
	},
	chipText: {
		backgroundColor: '#dbeafe',
		color: '#1e40af',
		height: 20,
		fontSize: 10,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: 0.3,
		borderRadius: 4,
	},
	chipTags: {
		backgroundColor: '#fef3c7',
		color: '#92400e',
		height: 20,
		fontSize: 10,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: 0.3,
		borderRadius: 4,
	},
	chipFiles: {
		backgroundColor: '#e9d5ff',
		color: '#6b21a8',
		height: 20,
		fontSize: 10,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: 0.3,
		borderRadius: 4,
	},
	chipPeople: {
		backgroundColor: '#d1fae5',
		color: '#065f46',
		height: 20,
		fontSize: 10,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: 0.3,
		borderRadius: 4,
	},
	chipFields: {
		backgroundColor: '#d1fae5',
		color: '#065f46',
		height: 20,
		fontSize: 10,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: 0.3,
		borderRadius: 4,
	},
	addFieldButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 'unset',
		padding: '8px 14px',
		backgroundColor: 'transparent',
		border: '2px dashed #cbd5e1',
		borderRadius: 10,
		color: '#64748b',
		fontSize: 14,
		fontWeight: 500,
		textTransform: 'none',
		marginTop: theme.spacing(1),
		transition: 'all 0.2s',
		'&:hover': {
		borderColor: '#1eb6a8',
		backgroundColor: '#f0fdf9',
		color: '#1eb6a8'
		}
	},
	addIcon: {
		fontSize: 16
	},
	connector: {
		position: "relative",
		top: "50%",
		right: 0,
		width: 30,
		height: 2,
		backgroundColor: "#cbd5e1",
		transform: "translateY(-50%)",
		display: "none",
		[theme.breakpoints.up("lg")]: {
			display: "block",
		},
		"&::before": {
			content: '""',
			position: "absolute",
			top: -4,
			right: -1,
			width: 0,
			height: 0,
			borderLeft: "6px solid #1eb6a8",
			borderTop: "5px solid transparent",
			borderBottom: "5px solid transparent",
		},
	},
	editBtn: {
		padding: "unset",
		minWidth: "fit-content",
		fontFamily: "Inter",
		fontWeight: 500,
		fontSize: "0.85rem",
		color: "#1eb6a8",
		textTransform: "none",
		textDecoration: "none",
		"&:hover": {
			background: "transparent",
		},
	},
}));

const Captions: { [key: string]: string } = {
    'Recent Activity': 'Shows new deal or update to existing deal',
    'Status': 'User can specify status in forwared emails',
    'Stage': 'Set by AI using email context',
    'Sectors': 'Set by AI using email context',
    'Key People': 'Founders and executives of the company',
    'Deck/Teaser': 'Captured from emails as PDFs or links',
    'Owner': 'Person that forwarded the deal',
    'Source': 'Person that originated the deal',
    'LinkedIn Posts': 'Captured from LinkedIn',
    'Communications': 'Emails exchanged with company',
    'Commentary': 'User can add comments in forwarded emails',
    'Description': 'AI-generated from context',
    'Location': 'Set by third-party data sources',
    'Sync Status': 'Auto-sync or manually sync to CRM',
    'Emails': 'Source emails forwarded for the deal',
};

const ChipType: { [key: string]: string } = {
    'Recent Activity': '',
    'Status': '',
    'Stage': '',
    // 'Sectors': 'TAGS',
    'Key People': 'PEOPLE',
    'Deck/Teaser': 'FILES',
    'Owner': 'PEOPLE',
    'Source': 'PEOPLE',
    'LinkedIn Posts': '',
    'Communications': 'TEXT',
    'Commentary': 'TEXT',
    'Description': 'TEXT',
    'Location': '',
    'Sync Status': '',
    'Emails': '',
};

const Enrichment: React.FC<{
	emailType: string,
    onOpenModal?: (field?: string) => void,
}> = ({ emailType, onOpenModal }) => {
    const classes = useStyles();
    const { tagGroups, customFields } = useContext(GroupSettingsContext);
    const { headers, setHeaders, visibleHeaders, setVisibleHeaders } = useContext(TableContext);
	const editableFields = useMemo(() => [
        'Status', 'Stage', ...tagGroups.map(({ title }) => title), ...customFields.map(({ name }) => name), 'Key People'
    ], [tagGroups, customFields]);

    const showColumn = useCallback((column: string) => !['Selection', 'Name', 'Updated', 'Added'].includes(column) && !column.startsWith('[') && !column.endsWith(']'), []);

    const handleCheck = useCallback((e: any, index: number) => {
        e.stopPropagation();
        e.preventDefault();
        setVisibleHeaders(prev => [...prev ?? []].map((state, i) => index === i ? !state : state));
    }, [setVisibleHeaders]);

    const dragAndDropStatus = useCallback((result: DropResult) => {
        const { source, destination, type } = result;

        if (!destination)
            return;

        switch (type) {
            case 'group': (() => {
                const sourceIdx = source.droppableId === 'manage-fields' ? source.index : -1;
                const destIdx = destination.droppableId === 'manage-fields' ? destination.index : -1;

                if (sourceIdx !== destIdx && destIdx >= 0) {
                    const newOrder = [...headers ?? []];
                    const nextVisible = [...visibleHeaders ?? []];
                    const movedHeader = newOrder.splice(sourceIdx, 1)[0];
                    const movedVisible = nextVisible.splice(sourceIdx, 1)[0];

                    newOrder.splice(destIdx, 0, movedHeader);
                    nextVisible.splice(destIdx, 0, movedVisible);
                    setHeaders(newOrder);
                    setVisibleHeaders(nextVisible);
                }
            })(); break;
        default:
            break;
        }
    // eslint-disable-next-line
    }, [headers, visibleHeaders]);

	const getChipClass = useCallback((type: string) => {
	  switch (type) {
		case 'TEXT': return classes.chipText;
		case 'TAGS': return classes.chipTags;
		case 'FILES': return classes.chipFiles;
		case 'PEOPLE': return classes.chipPeople;
		case 'FIELDS':
		default: return classes.chipFields;
	  }
	}, [classes]);

    return (<>
        <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%">
            <Stack className={classes.card} spacing={2} alignItems="flex-start" height="100%">
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <Box className={classes.mainIconContainer}>
                        <Db2Icon className={classes.mainIcon} />
                    </Box>
                    <Typography className={classes.title}>
                        {'Enrichment'}
                    </Typography>
                </Stack>
                <Typography fontFamily="Lato" fontSize="0.85rem" color={`#64748b`}>
                    {'Automatically populate deal fields'}
                </Typography>
                <Stack spacing={1} alignItems="flex-start" width="100%" height="100%">
					<Box className={classes.selectedTypeIndicator} width="100%">
						<Typography className={classes.selectedTypeText}>
							{`Showing: ${emailType}`}
						</Typography>
					</Box>
					<DragDropContext onDragEnd={dragAndDropStatus}>
						<Droppable droppableId="manage-fields" type="group" direction="vertical">
							{(provided) => (
								<Box className={classes.fieldsContainer} maxHeight="250px" width="100%" sx={{ overflowY: 'auto', ...scrollbarStyle}}
									{...provided.droppableProps} ref={provided.innerRef}>
									{!headers?.length ? (<FallbackLoading />) : headers?.map((field, i) => showColumn(field) && (
										<Draggable draggableId={'manage-field:' + field} index={i} key={'manage-fields-155-' + field}>
											{(provided) => (
												<Stack className={classes.field} alignItems="flex-start" justifyContent="center"
													{...provided.draggableProps} ref={provided.innerRef}
													style={{ ...provided.draggableProps.style }}
													key={'manage-fields-162-' + i}>
													<Stack spacing="4px" width="100%" onClick={(e) => handleCheck(e, i)} sx={{ cursor: 'pointer' }} >
														<Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
															<Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
																<Checkbox size="small"
																	className={classes.checkbox}
																	checked={visibleHeaders?.[i]}
																	disableRipple />
																<Typography color="textPrimary" className={classes.fieldName}>
																	{field === 'Key People' ? 'Team': field}
																</Typography>
																{ChipType[field] && (
																	<Chip className={getChipClass(ChipType[field])}
																		label={ChipType[field]} size="small" />
																)}
																{tagGroups.find(({title}) => title === field) && (
																	<Chip className={getChipClass('TAGS')}
																		label={'TAGS'} size="small" />
																)}
																{customFields.find(({name}) => name === field) && (
																	<Chip className={getChipClass('TEXT')}
																		label={'TEXT'} size="small" />
																)}
															</Stack>
															<Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
																{editableFields.includes(field) && (
																	<Button variant="text"
																		className={classes.editBtn}
																		onClick={(e) => {e.stopPropagation(); e.preventDefault(); onOpenModal?.(field)}}>
																		{'Edit'}
																	</Button>
																)}
																<Box {...provided.dragHandleProps} height={24}>
																	<DragIndicatorIcon fontSize="medium" sx={{ "&.MuiSvgIcon-root": { fill: '#666666' } }} />
																</Box>
															</Stack>
														</Stack>
														<Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%" pl="28px">
															<Typography color="textSecondary" className={classes.fieldDescription}>
																{Captions[field] || 'Assigned using AI'}
															</Typography>
														</Stack>
													</Stack>
												</Stack>
											)}
										</Draggable>
									))}
									{provided.placeholder}
								</Box>
							)}
						</Droppable>
					</DragDropContext>
					<AddCustomField
						onNewTag={() => onOpenModal?.('[[new-tag]]')}
						onNewField={() => onOpenModal?.('[[new-field]]')} />
                </Stack>
            </Stack>
			<Box className={classes.connector} />
        </Stack>
    </>);
}

export default Enrichment;
